import { GA4Channel, GA4EventPayload, GA4NotAvailable } from 'presentation/events/types/ga4Types';

type GA4BotChannel = GA4Channel;

/** Single source of truth for the Dibot GA4 event names. */
const GA4_BOT_EVENT_NAMES = [
	'bot_session_topic',
	'bot_escalated_session',
	'bot_product_search',
	'bot_search_result',
] as const;

type GA4BotEventName = (typeof GA4_BOT_EVENT_NAMES)[number];

type GA4BotEventPayload = GA4EventPayload & {
	event: GA4BotEventName;
	session_id: string | GA4NotAvailable;
	store_id: string | GA4NotAvailable;
	channel: GA4BotChannel;
};

/** Annex 1 — Bot Topic Catalog. Keys are literal dataLayer values (Spanish names preserved). */
export const GA4_BOT_TOPIC_CATALOG = {
	THD_Curador: { topicType: 'Custom', isMainTopic: 'true' },
	THD_SKU: { topicType: 'Custom', isMainTopic: 'false' },
	THD_Experto: { topicType: 'Custom', isMainTopic: 'true' },
	THD_ExpertoAjuste: { topicType: 'Custom', isMainTopic: 'true' },
	THD_ExpertoDuda: { topicType: 'Custom', isMainTopic: 'true' },
	THD_EstatusOrden_Rastreo: { topicType: 'Custom', isMainTopic: 'true' },
	THD_EstatusFolioCC: { topicType: 'Custom', isMainTopic: 'false' },
	THD_Concierge: { topicType: 'Custom', isMainTopic: 'true' },
	THD_Facturación: { topicType: 'Custom', isMainTopic: 'true' },
	THD_UbicacionTiendas: { topicType: 'Custom', isMainTopic: 'true' },
	THD_CambioTiendaSitio: { topicType: 'Custom', isMainTopic: 'false' },
	THD_Altisonantes: { topicType: 'Custom', isMainTopic: 'false' },
	THD_Soporte: { topicType: 'Custom', isMainTopic: 'false' },
	Despedida: { topicType: 'System', isMainTopic: 'false' },
	THD_Menu: { topicType: 'Custom', isMainTopic: 'true' },
	THD_GarantiaHusky: { topicType: 'Custom', isMainTopic: 'false' },
	THD_EjecutarTransferencia: { topicType: 'Custom', isMainTopic: 'false' },
	THD_Competencia: { topicType: 'Custom', isMainTopic: 'false' },
	'Al producirse un error': { topicType: 'System', isMainTopic: 'false' },
	'Fin de la conversación': { topicType: 'System', isMainTopic: 'false' },
} as const;

type GA4BotTopic = keyof typeof GA4_BOT_TOPIC_CATALOG;

/**
 * Correlated union: for a given `topic`, `topic_type` and `is_main_topic` can ONLY be
 * the values the catalog defines for it. The 'NA' branch covers retrieval failures as
 * an all-or-nothing fallback (if the bot knows the topic, the catalog resolves the rest).
 */
export type GA4BotTopicPayload =
	| {
			[K in GA4BotTopic]: {
				topic: K;
				topic_type: (typeof GA4_BOT_TOPIC_CATALOG)[K]['topicType'];
				is_main_topic: (typeof GA4_BOT_TOPIC_CATALOG)[K]['isMainTopic'];
			};
	  }[GA4BotTopic]
	| { topic: GA4NotAvailable; topic_type: GA4NotAvailable; is_main_topic: GA4NotAvailable };

/**
 * Events
 */
export type GA4BotSessionTopicEvent = GA4BotEventPayload &
	GA4BotTopicPayload & {
		event: Extract<GA4BotEventName, 'bot_session_topic'>;
		interaction_with: string;
		interaction_date: string;
	};

/** 'true' | 'false' with an NA fallback (see Annex open question on successful_transfer). */
export type GA4BotSuccessfulTransfer = 'true' | 'false' | GA4NotAvailable;

export type GA4BotEscalatedSessionEvent = GA4BotEventPayload & {
	event: Extract<GA4BotEventName, 'bot_escalated_session'>;
	topic: GA4BotTopic | GA4NotAvailable;
	user_input: string;
	bot_message: string;
	successful_transfer: GA4BotSuccessfulTransfer;
};

export type GA4BotProductSearchEventPayload = GA4BotEventPayload & {
	event: Extract<GA4BotEventName, 'bot_product_search'>;
	topic: Extract<GA4BotTopic, 'THD_Curador' | 'THD_SKU'>;
	product: string;
};

export type GA4BotSearchResultsEventPayload = GA4BotEventPayload & {
	event: Extract<GA4BotEventName, 'bot_search_result'>;
	products: string[];
};

export type GA4BotEventMobilePayload =
	| GA4BotSessionTopicEvent
	| GA4BotEscalatedSessionEvent
	| GA4BotProductSearchEventPayload
	| GA4BotSearchResultsEventPayload;

export type GA4BotSearchTopic = Extract<GA4BotTopic, 'THD_Curador' | 'THD_SKU'>;

export type SendBotEventBaseProps = {
	sessionId: string | null;
};

export type SendBotSessionTopicEventProps = SendBotEventBaseProps & {
	/** Raw topic string from `customEvent.customEventValue`; resolved against the catalog. */
	topic: string;
	interactionWith: string;
	interactionDate: string;
};

export type SendBotEscalatedSessionEventProps = SendBotEventBaseProps & {
	/** Raw topic string from `customEvent.customEventValue`; resolved against the catalog. */
	topic: string;
	userInput: string;
	botMessage: string;
	successfulTransfer: GA4BotSuccessfulTransfer;
};

export type SendBotProductSearchEventProps = SendBotEventBaseProps & {
	topic: GA4BotSearchTopic;
	product: string;
};

export type SendBotSearchResultEventProps = SendBotEventBaseProps & {
	products: string[];
};

export type DibotProductCardKind = 'primary' | 'secondary';
